import Tippy from '@tippyjs/react'
import { type CSSProperties, type ReactNode, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { classNameParser, useOutsideClick } from '../../../../helpers'
import type { InputProps } from '../../../../types'
import { getValidSelection, initializeSelectInputState, isElementVisible, selectInputReducer } from './helpers'
import type { SelectInputOptionType } from './types'

import '../../../../styles/input.css'
import './style.css'

import helpImage from '../../../../images/ic-help.svg'

type SelectInputProps = InputProps<
  string | number,
  {
    native?: boolean
    options: SelectInputOptionType<string | number>[]
    optional?: boolean
    fieldInfoSize?: 'small' | 'large'
    fieldInfo?: ReactNode
  }
>

// FIXME: preselect by keyboard

export const SelectInput = (props: SelectInputProps) => {
  const inputRef = useOutsideClick<HTMLInputElement>(null, () => dispatch({ type: 'close' }))
  const [infoTarget, setInfoTarget] = useState<HTMLElement>()
  const [fieldTarget, setFieldTarget] = useState<HTMLElement>()
  const optionsRef = useRef<HTMLUListElement | null>(null)

  const placeHolder = props.placeHolder || 'Select'

  let [{ activeIndex, open, selected }, dispatch] = useReducer(
    selectInputReducer(props.options),
    props,
    initializeSelectInputState,
  )

  // provide default value if component is not yet selected
  if (!selected && props.defaultValue) {
    const defaultValue = String(props.defaultValue)
    selected = props.options.find((item, i) => {
      // biome-ignore lint/suspicious/noDoubleEquals: both operands are strings
      if (String(item.value) == defaultValue) {
        if (activeIndex == null) activeIndex = i
        return true
      }
    })
  }
  // make sure selected item is in options, otherwise discard selected item
  // (in case options change we should forget selection)
  // cannot do that in effect since options can change superficially, not deeply
  else if (selected) {
    const value = String(selected.value)
    selected = props.options.find((item, i) => {
      // biome-ignore lint/suspicious/noDoubleEquals: both operands are strings
      if (String(item.value) == value) {
        if (activeIndex == null) activeIndex = i
        return true
      }
    })
  }

  // NOTE: be careful if decide to wrap into useCallback: dispatch can be a dependency
  const handleSelect = (value?: string | number) => {
    if (value == null) return
    const validSelection = getValidSelection(props.options, activeIndex, value)
    props.onChange && props.onChange(validSelection.value)
    dispatch({ type: 'select', payload: { selected: validSelection } })
    dispatch({ type: 'close' })
  }

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      dispatch({ type: 'close' })
      return
    }

    e.preventDefault()
    switch (e.key) {
      case 'ArrowDown':
        dispatch({ type: 'setNextActive' })
        break
      case 'ArrowUp':
        dispatch({ type: 'setPreviousActive' })
        break
      case 'Enter':
        handleSelect(props.options[activeIndex || 0].value)
        break
      case 'Escape':
        dispatch({ type: 'close' })
        break
      default:
        // find first active item starting with the key
        // it's safe to say 1-length keys are direct characters or space
        // ref: https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_key_values
        if (e.key.length === 1) {
          const index = props.options.findIndex(
            (item) => item.label[0].toLocaleLowerCase() === e.key.toLocaleLowerCase(),
          )
          if (index >= 0) dispatch({ type: 'setActive', payload: { index } })
        }
        break
    }
  }

  useEffect(() => {
    if (!open) return
    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [open, handleKeyPress])

  // update scroll whenever active item is hovered
  useEffect(() => {
    if (!optionsRef.current) return
    const list = optionsRef.current as HTMLElement
    const active = list.querySelector('.active') as HTMLElement | null
    if (!active) return

    if (!isElementVisible(active, list)) list.scrollTop = active.offsetTop - 33
  }, [optionsRef.current, open, activeIndex])

  const isTopview = useMemo(() => {
    if (!inputRef.current) return false

    const { y, height: INPUT_HEIGHT } = inputRef.current.getBoundingClientRect()
    const DROPDOWN_HEIGHT = props.options.length >= 4 ? 192 : props.options.length * INPUT_HEIGHT

    const bottomRest = window.innerHeight - y - INPUT_HEIGHT

    return bottomRest < DROPDOWN_HEIGHT && (y > DROPDOWN_HEIGHT || y > bottomRest)
  }, [open])

  const getTopIndent = (): CSSProperties => {
    if (!inputRef.current || !isTopview) return {}

    const { height: INPUT_HEIGHT } = inputRef.current.getBoundingClientRect()
    const DROPDOWN_HEIGHT = props.options.length >= 4 ? 200 : props.options.length * INPUT_HEIGHT + 8

    return {
      top: -DROPDOWN_HEIGHT,
    }
  }

  return (
    <div
      id={props.id}
      key={props.key}
      className={classNameParser('global--input-container', props.className)}
      style={props.style}
    >
      <div className="flex justify-between items-end">
        {props.label ? (
          <label htmlFor={props.id} className="global--input-label">
            {props.label}
          </label>
        ) : (
          <div></div>
        )}
        <div className="flex flex-row items-end">
          {props.optional === true ? (
            <p className={`global--input-label font-400 !text-[12px] mr-[4px]`}>Optional field</p>
          ) : null}
        </div>
      </div>
      <Tippy
        reference={fieldTarget}
        triggerTarget={infoTarget}
        disabled={!props.fieldInfo}
        content={
          <div className="global--tooltip-content bg-white w-full rounded-md shadow-2xl px-[16px] py-[4px]">
            {props.fieldInfo}
          </div>
        }
      />
      <div
        ref={(el) => {
          if (el) setFieldTarget(el)
        }}
        className="input--select-field relative"
        title={selected ? selected.label : props.label}
      >
        {/* biome-ignore lint/a11y/noStaticElementInteractions: custom dropdown trigger, focusable through tabIndex */}
        <div
          className={`${
            props.native == null ? 'sm:hidden' : props.native ? 'hidden' : ''
          } input--select-input ${selected ? 'global--with-value' : ''} ${
            props.fieldInfo !== undefined ? '!pr-[50px]' : ''
          }  ${props.disabled ? 'disabled' : ''}`}
          ref={inputRef}
          // toggle logic here, we can't do that via onClick and onFocus
          onMouseDown={(e) => (e.stopPropagation(), !props.disabled && dispatch({ type: open ? 'close' : 'open' }))}
          onTouchStart={(e) => (e.stopPropagation(), !props.disabled && dispatch({ type: open ? 'close' : 'open' }))}
          onFocus={(e) => (e.stopPropagation(), !props.disabled && dispatch({ type: 'open' }))}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: custom dropdown trigger, focusable for keyboard users
          tabIndex={0}
        >
          {selected?.label || placeHolder}
        </div>
        <select
          className={`${
            props.native == null ? 'hidden sm:block' : !props.native ? 'hidden' : 'block'
          } input--select-input-mobile ${selected ? 'global--with-value' : ''}`}
          value={selected?.value != null ? selected.value : ''}
          onChange={(e) => handleSelect(e.target.value)}
          // biome-ignore lint/a11y/noAutofocus: opt-in through the autoFocus prop
          autoFocus={props.autoFocus}
          disabled={props.disabled}
        >
          <option disabled value="">
            {placeHolder}
          </option>
          {props.options.map((item: (typeof props.options)[number]) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <div
          className={`input--select-field-node select-none ${
            props.fieldInfo ? 'right-[45px]' : 'right-[21px]'
          } ${open ? '!bg-[#2E61EF]' : ''}`}
        ></div>
        {props.fieldInfo ? (
          <div
            ref={(el) => {
              if (el) setInfoTarget(el)
            }}
            className="absolute right-0 top-0"
          >
            <img src={helpImage.src} alt="help icon" width={48} height={48}></img>
          </div>
        ) : null}
        {open ? (
          <div className={`input--select-input-options-box`} style={getTopIndent()}>
            <ul ref={optionsRef} className="input--select-input-options">
              {props.options.map((item: (typeof props.options)[number], index: number) => (
                // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard selection handled by the trigger and the native <select>
                // biome-ignore lint/a11y/useKeyWithMouseEvents: keyboard selection handled by the trigger and the native <select>
                <li
                  className={`input--select-input-option ${activeIndex === index ? 'active' : ''}`}
                  key={item.value}
                  onClick={() => handleSelect(item.value)}
                  onTouchStart={() => dispatch({ type: 'setActive', payload: { index } })}
                  onMouseOver={() => dispatch({ type: 'setActive', payload: { index } })}
                >
                  {item.label}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      {props.error && <p className="global--input-error">{props.error}</p>}
    </div>
  )
}
